import React, { useState, useRef, useEffect } from 'react';

interface DropdownProps {
  className?: string;
  children?: React.ReactNode;
}

function DropdownBase({ className = '', children }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Collect align prop from Menu child to apply dropdown-end on the container
  let alignClass = '';
  React.Children.forEach(children, child => {
    if (React.isValidElement(child) && (child as React.ReactElement<any>).props.align === 'end') {
      alignClass = 'dropdown-end';
    }
  });

  return (
    <div className={`dropdown ${open ? 'dropdown-open' : ''} ${alignClass} ${className}`.trim()} ref={ref}>
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child as React.ReactElement<any>, { _open: open, _setOpen: setOpen });
      })}
    </div>
  );
}

interface ToggleProps {
  variant?: string;
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
  _open?: boolean;
  _setOpen?: (open: boolean) => void;
}

function DropdownToggle({ variant = 'primary', className = '', children, _open, _setOpen, ...rest }: ToggleProps) {
  return (
    <button
      type="button"
      className={`btn btn-${variant} ${className}`.trim()}
      onClick={() => _setOpen?.(!_open)}
      aria-expanded={_open}
      {...rest}
    >
      {children}
    </button>
  );
}

interface MenuProps {
  align?: 'end' | 'start';
  className?: string;
  children?: React.ReactNode;
  role?: string;
  'aria-label'?: string;
  style?: React.CSSProperties;
  _open?: boolean;
  _setOpen?: (open: boolean) => void;
}

function DropdownMenu({ align, className = '', children, _open, _setOpen, style, ...rest }: MenuProps) {
  if (!_open) return null;
  return (
    <ul
      className={`dropdown-content menu bg-base-100 rounded-box shadow-lg z-10 w-52 ${className}`.trim()}
      style={style}
      {...rest}
    >
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child as React.ReactElement<any>, { _setOpen });
      })}
    </ul>
  );
}

interface ItemProps {
  as?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
  to?: string;
  onClick?: React.MouseEventHandler;
  _setOpen?: (open: boolean) => void;
  [key: string]: any;
}

function DropdownItem({ as: Tag = 'button', className = '', children, _setOpen, onClick, ...rest }: ItemProps) {
  const handleClick = (e: React.MouseEvent) => {
    onClick?.(e);
    _setOpen?.(false);
  };
  const tagProps = Tag === 'button' ? { type: 'button' as const } : {};
  return (
    <li>
      <Tag className={`${className}`.trim()} onClick={handleClick} {...tagProps} {...rest}>
        {children}
      </Tag>
    </li>
  );
}

function DropdownDivider({ className = '' }: { className?: string }) {
  return <li className={`divider ${className}`.trim()}></li>;
}

const Dropdown = Object.assign(DropdownBase, {
  Toggle: DropdownToggle,
  Menu: DropdownMenu,
  Item: DropdownItem,
  Divider: DropdownDivider,
});

export default Dropdown;
